import cs from "classnames";
import React from "react";
import { GoChevronDown, GoChevronRight } from "react-icons/go";

import { PatientLink } from "src/features/patient";
import { FormTreeAction } from "src/modules/shared/form-explorer/reducer";
import {
	FilteredLeafNode,
	FilteredNode,
	FilteredParentNode
} from "src/modules/shared/form-explorer/types";
import {
	isFilteredParentNode,
	isLocalizedParentNode
} from "src/modules/shared/form-explorer/utils";

import "./FormTree.css";

const emptyPath: string[] = [];

interface FormTreeProps {
	isForceOpen: boolean;
	nodes: FilteredNode[];
	dispatch: React.Dispatch<FormTreeAction>;
}

export const FormTree: React.FunctionComponent<FormTreeProps> = ({
	isForceOpen,
	nodes,
	dispatch
}) => (
	<div className="form-tree">
		{nodes.map((node, i) => (
			<NodeRenderer
				key={i}
				isForceOpen={isForceOpen}
				parentPath={emptyPath}
				level={0}
				node={node}
				dispatch={dispatch}
			/>
		))}
	</div>
);

interface NodeRendererProps {
	isForceOpen: boolean;
	parentPath: string[];
	level: number;
	node: FilteredNode;
	dispatch: React.Dispatch<FormTreeAction>;
}

const NodeRenderer: React.FunctionComponent<NodeRendererProps> = ({
	isForceOpen,
	parentPath,
	level,
	node,
	dispatch
}) =>
	isLocalizedParentNode(node) ? (
		<ParentNodeRenderer
			isForceOpen={isForceOpen}
			level={level}
			parentPath={parentPath}
			node={node}
			dispatch={dispatch}
		/>
	) : (
		<LeafNodeRenderer level={level} node={node} />
	);

interface ParentNodeRendererProps {
	isForceOpen: boolean;
	parentPath: string[];
	level: number;
	node: FilteredParentNode;
	dispatch: React.Dispatch<FormTreeAction>;
}

const ParentNodeRenderer: React.FunctionComponent<ParentNodeRendererProps> = ({
	isForceOpen,
	parentPath,
	level,
	node,
	dispatch
}) => {
	const {
		id,
		isOpen,
		isVisible,
		value: { title },
		children
	} = node;
	const path = React.useMemo(
		() => parentPath.concat(node.id),
		[parentPath, node]
	);
	const onToggle = React.useCallback(() => {
		dispatch({ type: "toggle", payload: { path } });
	}, [dispatch, path]);
	return (
		<>
			<div
				className={cs("form-tree-node", "form-tree-node--parent", {
					"form-tree-node--open": isOpen || isForceOpen,
					"form-tree-node--visible": isVisible,
					"form-tree-node--important": children.some(isImportant)
				})}
				style={{ paddingLeft: `${15 + 25 * level}px` }}
			>
				<button
					className="form-tree-node__button"
					onClick={onToggle}
					disabled={isForceOpen}
				>
					{isOpen || isForceOpen ? (
						<GoChevronDown />
					) : (
						<GoChevronRight />
					)}
				</button>
				<div className="form-tree-node__text">
					<PatientLink to={`/form/${id}`}>{title}</PatientLink>
				</div>
			</div>
			<div className="form-tree-node-list">
				{children.map((node, i) => (
					<NodeRenderer
						key={i}
						parentPath={path}
						isForceOpen={isForceOpen}
						level={level + 1}
						node={node}
						dispatch={dispatch}
					/>
				))}
			</div>
		</>
	);
};

interface LeafNodeRendererProps {
	level: number;
	node: FilteredLeafNode;
}

const LeafNodeRenderer: React.FunctionComponent<LeafNodeRendererProps> = ({
	level,
	node: {
		id,
		isVisible,
		value: { title, isDummy = false, isImportant = false }
	}
}) => (
	<div
		className={cs("form-tree-node", "form-tree-node--leaf", {
			"form-tree-node--disabled": isDummy,
			"form-tree-node--visible": isVisible,
			"form-tree-node--important": isImportant
		})}
		style={{ paddingLeft: `${15 + 25 * level}px` }}
	>
		<div className="form-tree-node__text">
			<PatientLink to={`/form/${id}`}>{title}</PatientLink>
		</div>
	</div>
);

function isImportant(node: FilteredNode): boolean {
	return isFilteredParentNode(node)
		? node.children.some(isImportant)
		: node.value.isImportant === true;
}
